import { MdFileUpload, MdDescription, MdTableChart, MdPictureAsPdf, MdClose, MdDelete, MdDownload, MdImage } from "react-icons/md";
import Card from "components/card";
import React, { useRef, useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { API_BASE_URL } from "utils/api";
import { usePermission } from "hooks/usePermission";

const ACCEPTED = ".pdf,.doc,.docx,.xls,.xlsx,.xlsm,.csv,.jpg,.jpeg,.png,.gif";
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB (match UI)

const formatBytes = (bytes) => {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const getFileNameFromPath = (path) => {
  return path.split("/").pop();
};

const FileIcon = ({ name }) => {
  const ext = name.split(".").pop().toLowerCase();
  if (["jpg", "jpeg", "png", "gif"].includes(ext))
    return (
      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-yellow-50 dark:bg-yellow-900/20">
        <MdImage className="h-5 w-5 text-yellow-600" />
      </span>
    );
  if (ext === "pdf")
    return (
      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-50 dark:bg-red-900/20">
        <MdPictureAsPdf className="h-5 w-5 text-red-500" />
      </span>
    );
  if (["xls", "xlsx", "xlsm", "csv"].includes(ext))
    return (
      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-green-50 dark:bg-green-900/20">
        <MdTableChart className="h-5 w-5 text-green-600" />
      </span>
    );
  return (
    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 dark:bg-blue-900/20">
      <MdDescription className="h-5 w-5 text-blue-500" />
    </span>
  );
};

const Upload = ({ lead }) => {
  const { can } = usePermission();
  const canCreateLead = can("lead_management", "create");
  const canDeleteLead = can("lead_management", "delete");
  const leadId = lead?._id;
  const inputRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const pendingSectionRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deletingDocPath, setDeletingDocPath] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, documentPath: null });
  const [documents, setDocuments] = useState(lead?.documents || []);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });

  // Auto-hide toast after 3 seconds
  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => {
        setToast({ show: false, message: "", type: "" });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toast.show]);

  const showToast = (message, type) => {
    setToast({ show: true, message, type });
  };

  const openDeleteConfirm = (documentPath) => {
    setDeleteConfirm({ show: true, documentPath });
  };

  const closeDeleteConfirm = () => {
    setDeleteConfirm({ show: false, documentPath: null });
  };

  useEffect(() => {
    const appRoot = document.getElementById("root");
    if (deleteConfirm.show) {
      document.body.style.overflow = "hidden";
      if (appRoot) appRoot.style.filter = "blur(8px)";
    } else {
      document.body.style.overflow = "";
      if (appRoot) appRoot.style.filter = "";
    }
    return () => {
      document.body.style.overflow = "";
      if (appRoot) appRoot.style.filter = "";
    };
  }, [deleteConfirm.show]);

  useEffect(() => {
    setDocuments(lead?.documents || []);
  }, [lead?.documents]);

  // Auto-scroll to pending documents when files are added
  useEffect(() => {
    if (files.length > 0 && scrollContainerRef.current && pendingSectionRef.current) {
      setTimeout(() => {
        pendingSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }, 100);
    }
  }, [files.length]);

  /* ---------- VALIDATION ---------- */

  // Validate a file. Some cloud providers (mobile pickers) may provide files
  // with size=0; we attempt to read the file content asynchronously to
  // determine a real size when possible.
  const validateFileAsync = async (file) => {
    const name = file.name || "unnamed";
    const ext = (name.split(".").pop() || "").toLowerCase();

    const allowedExt = [
      "pdf",
      "doc",
      "docx",
      "xls",
      "xlsx",
      "xlsm",
      "csv",
      "jpg",
      "jpeg",
      "png",
      "gif",
    ];

    if (!allowedExt.includes(ext)) {
      showToast(`${name} is not a supported format`, "error");
      return false;
    }

    let size = file.size || 0;

    // If size is 0, try to read the file content which can help for some
    // mobile/cloud provider file pickers that expose blobs but report size=0.
    if (size === 0 && typeof file.arrayBuffer === "function") {
      try {
        const buffer = await file.arrayBuffer();
        size = buffer.byteLength || 0;
      } catch (err) {
        // fallback: leave size as 0 and continue to validation which will
        // likely reject based on size
        console.warn("Failed reading file arrayBuffer:", err);
      }
    }

    if (size === 0) {
      // If still zero, reject — most real files should have a size > 0.
      showToast(`${name} appears empty or is not accessible`, "error");
      return false;
    }

    if (size > MAX_FILE_SIZE) {
      showToast(`${name} exceeds ${formatBytes(MAX_FILE_SIZE)} limit`, "error");
      return false;
    }

    return true;
  };

  /* ---------- HANDLE FILES ---------- */

  const handleFiles = async (incoming) => {
    const newFiles = [];

    for (const file of Array.from(incoming)) {
      const ok = await validateFileAsync(file);
      if (!ok) continue;

      const size = file.size || 0;

      const exists = files.some(
        (f) => f.name === file.name && f.size === size
      );

      if (!exists) {
        newFiles.push({
          file,
          name: file.name,
          size,
          id: `${file.name}-${size}-${Date.now()}`,
        });
      }
    }

    if (newFiles.length) setFiles((prev) => [...prev, ...newFiles]);
  };

  const handleChange = async (e) => {
    if (e.target.files?.length) {
      await handleFiles(e.target.files);
      e.target.value = "";
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    if (e.dataTransfer.files?.length) await handleFiles(e.dataTransfer.files);
  };

  const handleRemove = (id) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  /* ---------- UPLOAD ---------- */

  const handleUpload = async () => {
    if (!files.length) return;

    try {
      setUploading(true);
      const token = localStorage.getItem("token");

      const formData = new FormData();
      formData.append("name", lead?.name);
      formData.append("email", lead?.email);
      formData.append("phone", lead?.phone);

      files.forEach((f) => {
        formData.append("documents", f.file);
      });

      const response = await fetch(`${API_BASE_URL}/leads/${leadId}/documents`, {
        method: "PUT",
        headers: {
          "Authorization": `Bearer ${token}`,
        },
        body: formData
      });

      const responseText = await response.text();
      let responseData;
      try {
        responseData = responseText ? JSON.parse(responseText) : null;
      } catch (parseError) {
        responseData = { text: responseText };
      }

      if (response.ok) {
        showToast("Documents uploaded successfully", "success");

        if (responseData?.data?.documents) {
          setDocuments(responseData.data.documents);
        }

        setFiles([]);
      } else {
        const errorMessage = responseData?.message || responseData?.error || "Upload failed";
        showToast(errorMessage, "error");
      }
    } catch (err) {
      console.error(err);
      showToast("Upload failed", "error");
    } finally {
      setUploading(false);
    }
  };
  const confirmDeleteDocument = async () => {
    if (!deleteConfirm.documentPath) return;
    closeDeleteConfirm();
    await handleDeleteDocument(deleteConfirm.documentPath);
  };
  /* ---------- DELETE DOCUMENT ---------- */

  const handleDeleteDocument = async (docPath) => {
    try {
      setDeleting(true);
      setDeletingDocPath(docPath);
      const token = localStorage.getItem("token");

      const response = await fetch(`${API_BASE_URL}/leads/${leadId}/documents`, {
        method: "DELETE",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ documentPath: docPath })
      });

      if (response.ok) {
        showToast("Document deleted successfully", "success");
        setDocuments((prev) => prev.filter((doc) => doc !== docPath));
      } else {
        showToast("Failed to delete document", "error");
      }
    } catch (err) {
      console.error(err);
      showToast("Delete failed", "error");
    } finally {
      setDeleting(false);
      setDeletingDocPath(null);
    }
  };

  /* ---------- DOWNLOAD DOCUMENT ---------- */

  const handleDownloadDocument = (docPath) => {
    const url = `${API_BASE_URL}${docPath}`;
    window.open(url, '_blank');
  };

  const existingDocuments = documents;

  return (
    <Card extra="w-full h-full p-4 flex flex-col gap-3 overflow-hidden">
      {/* ── Upload zone (always visible, never grows) ── */}
      {canCreateLead ? (
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
          className="flex flex-col items-center justify-center rounded-xl border-[2px] border-dashed border-gray-200 bg-lightPrimary py-5 dark:border-navy-700 dark:bg-navy-700"
        >
          <MdFileUpload className="text-[48px] text-brand-500 dark:text-white" />
          <h4 className="mt-1 text-sm font-bold text-brand-500 dark:text-white">
            Upload Documents
          </h4>

          <input
            ref={inputRef}
            type="file"
            multiple
            accept={ACCEPTED}
            className="hidden"
            onChange={handleChange}
          />

          <button
            onClick={() => inputRef.current?.click()}
            className="mt-3 flex items-center gap-2 rounded-xl bg-brand-500 px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-600"
          >
            <MdFileUpload className="h-4 w-4" />
            Select Files
          </button>

          {files.length > 0 && (
            <button
              onClick={handleUpload}
              disabled={uploading}
              className="mt-2 rounded-xl bg-green-500 px-4 py-1.5 text-sm text-white hover:bg-green-600 disabled:opacity-50"
            >
              {uploading ? "Uploading..." : "Upload Documents"}
            </button>
          )}

          <p className="mt-2 text-center text-[11px] text-gray-500">
            PDF · Word · Excel · Images (JPG/PNG/GIF) (max 10MB)<br />
          </p>
          <p className="mt-1 text-center text-[11px] text-gray-500">
            Only allow 10 files to upload at a time.
          </p>
        </div>
      ) : (
        <div className="rounded-xl border-[2px] border-dashed border-gray-200 bg-lightPrimary py-5 px-4 text-center text-sm text-gray-500 dark:border-navy-700 dark:bg-navy-700 dark:text-gray-300">
          Document upload is hidden because you do not have lead creation permission.
        </div>
      )}

      {(existingDocuments.length > 0 || files.length > 0) && (
        <div ref={scrollContainerRef} className="max-h-[360px] overflow-y-auto flex flex-col gap-3 pr-1">
          {/* Existing Documents Section */}
          {existingDocuments.length > 0 && (
            <div className="flex flex-col gap-2">
              <p className="text-xs font-semibold text-navy-700 dark:text-white">
                Uploaded Documents ({existingDocuments.length})
              </p>

              <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {existingDocuments.map((docPath, idx) => {
                  const fileName = getFileNameFromPath(docPath);
                  return (
                    <li
                      key={`existing-${idx}`}
                      className="flex items-center gap-2 rounded-xl border bg-gray-50 px-2 py-2 dark:bg-navy-800 dark:border-navy-600"
                    >
                      <FileIcon name={fileName} />

                      <div className="min-w-0 flex-1">
                        <p className="truncate text-xs font-medium dark:text-white">
                          {fileName}
                        </p>
                        <p className="text-[10px] text-gray-400">Uploaded</p>
                      </div>

                      <div className="flex gap-1">
                        <button
                          onClick={() => handleDownloadDocument(docPath)}
                          className="rounded-full p-1 text-gray-400 hover:text-blue-500 transition-colors"
                          title="Download"
                        >
                          <MdDownload className="h-3.5 w-3.5" />
                        </button>
                        {canDeleteLead && (
                          <button
                            onClick={() => openDeleteConfirm(docPath)}
                            disabled={deleting}
                            className="rounded-full p-1 text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50"
                            title="Delete"
                          >
                            {deleting && deletingDocPath === docPath ? (
                              <span className="inline-flex h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                            ) : (
                              <MdClose className="h-3.5 w-3.5" />
                            )}
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Pending Upload Section */}
          {files.length > 0 && (
            <div ref={pendingSectionRef} className="flex flex-col gap-2 border-t pt-3 dark:border-navy-600">
              <p className="text-xs font-semibold text-navy-700 dark:text-white">
                Pending Upload ({files.length})
              </p>

              <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {files.map((f) => (
                  <li
                    key={f.id}
                    className="flex items-center gap-2 rounded-xl border-2 border-dashed bg-blue-50 px-2 py-2 dark:bg-blue-900/10 dark:border-blue-700"
                  >
                    <FileIcon name={f.name} />

                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs font-medium dark:text-white">
                        {f.name}
                      </p>
                      <p className="text-[10px] text-gray-400">
                        {formatBytes(f.size)}
                      </p>
                    </div>

                    <button
                      onClick={() => handleRemove(f.id)}
                      className="rounded-full p-1 text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <MdClose className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* ── Empty State ── */}
      {existingDocuments.length === 0 && files.length === 0 && (
        <div className="flex flex-col gap-2">
          <p className="text-xs font-semibold text-navy-700 dark:text-white">
            No Documents
          </p>
          <div className="flex h-[120px] flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-gray-200 dark:border-navy-600">
            <MdDescription className="h-8 w-8 text-gray-300" />
            <p className="text-sm text-gray-400">
              No documents attached yet
            </p>
          </div>
        </div>
      )}


      {/* ── Toast Notification ── */}
      {toast.show && (
        <div
          className={`fixed top-24 right-4 px-4 py-3 rounded-lg shadow-lg transition-all duration-300 z-50 ${toast.type === "success"
            ? "bg-green-100 text-green-800 border border-green-300"
            : "bg-red-100 text-red-800 border border-red-300"
            }`}
        >
          <p className="text-sm font-medium">{toast.message}</p>
        </div>
      )}

      {deleteConfirm.show && ReactDOM.createPortal(
        <div
          className="fixed inset-0 z-[100000] flex items-center justify-center p-4"
          onClick={closeDeleteConfirm}
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-xl" />
          <div
            className="relative z-[100001] w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl dark:bg-navy-800"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-lg font-semibold text-navy-900 dark:text-white">Delete document</p>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                  Are you sure you want to delete <span className="font-semibold">{getFileNameFromPath(deleteConfirm.documentPath || "")}</span>? This action cannot be undone.
                </p>
              </div>
              <button
                type="button"
                onClick={confirmDeleteDocument}
                disabled={deleting}
                className="rounded-2xl bg-red-600 p-3 text-white hover:bg-red-700 disabled:opacity-50"
                title="Delete"
              >
                <MdDelete className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeDeleteConfirm}
                disabled={deleting}
                className="rounded-2xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 dark:border-navy-600 dark:text-slate-200 dark:hover:bg-navy-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeleteDocument}
                disabled={deleting}
                className="rounded-2xl bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </Card>
  );
};

export default Upload;
